import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { TRAINER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { GymLoader } from "../../components/common/GymLoader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "../../components/ui/sonner";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// UX-TR-01 §1 SCR-P06-11: reuses Prompt 04's real `availability` shape verbatim (windows +
// exceptions), just always own-provider-scoped server-side — no provider-id input needed here,
// unlike the Manager tier's Roster.jsx (which enters ANY trainer's ref manually).
export default function Availability() {
  const reducedMotion = useReducedMotion();
  const [windows, setWindows] = useState([]);
  const [day, setDay] = useState("mon");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("18:00");

  const availQ = useQuery({ queryKey: ["trainer-availability"], queryFn: async () => (await api.get("/trainer/availability")).data });
  useEffect(() => { if (availQ.data) setWindows(availQ.data.windows); }, [availQ.data]);

  const saveMutation = useMutation({
    mutationFn: () => api.post("/trainer/availability", { windows, exceptions: availQ.data?.exceptions || [] }),
    onSuccess: () => toast.success("Availability saved."),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (availQ.isLoading) return <GymLoader fullPage label="Loading availability…" testId="trainer-availability-loader" />;

  return (
    <div data-testid={TRAINER.availabilityPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>My availability</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>When you're bookable for 1:1 PT sessions.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        {windows.map((w, i) => (
          <div key={i} data-testid={TRAINER.availabilityWindowRow(i)} className="flex items-center justify-between text-sm rounded-[8px] px-3 py-2" style={{ background: "#F4F3EF" }}>
            <span style={{ color: "#141416" }}>{w.day.toUpperCase()}: {w.start}–{w.end}</span>
            <button onClick={() => setWindows(windows.filter((_, idx) => idx !== i))}><Trash2 size={13} style={{ color: "#68686E" }} /></button>
          </div>
        ))}
        <div className="flex gap-2 items-end">
          <Select onValueChange={setDay} value={day}>
            <SelectTrigger data-testid={TRAINER.availabilityDaySelect}><SelectValue /></SelectTrigger>
            <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d.toUpperCase()}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid={TRAINER.availabilityStartInput} type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          <Input data-testid={TRAINER.availabilityEndInput} type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          <Button data-testid={TRAINER.availabilityAddWindowButton} variant="outline" onClick={() => setWindows((prev) => [...prev, { day, start, end }])}><Plus size={14} /></Button>
        </div>
        <Button
          data-testid={TRAINER.availabilitySaveButton}
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          className="w-full text-white" style={{ background: "#D6202B" }}
        >
          Save availability
        </Button>
      </motion.div>
    </div>
  );
}
